import { Box, Fade, Typography } from '@mui/material';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { usePrefersReducedMotion } from '../../../hooks/use-prefers-reduced-motion';

const BAR_COUNT = 32;
const MIN_BAR_HEIGHT_PX = 6;
const MAX_BAR_HEIGHT_PX = 24;
const STRIP_LOOP_SECONDS = 6;
const BAR_PULSE_SECONDS = 1.8;
/** Stagger step so neighboring bars pulse out of phase, like syllables. */
const BAR_PULSE_STAGGER_SECONDS = 0.12;
const BAR_PULSE_PHASES = 7;

/**
 * Deterministic bar heights: a symmetric half-sine envelope (short at both
 * ends, tall in the middle — the classic voice bubble) with a small syllable
 * flutter layered on top. Fixed values (computed once at module load, stable
 * ids) keep the animations running across re-renders instead of restarting
 * them.
 */
const BAR_PATTERN = Array.from({ length: BAR_COUNT }, (_, barIndex) => ({
  barId: `wave-bar-${barIndex}`,
  heightPx: Math.round(
    Math.min(
      MAX_BAR_HEIGHT_PX,
      Math.max(
        MIN_BAR_HEIGHT_PX,
        6 +
          16 * Math.sin((Math.PI * barIndex) / (BAR_COUNT - 1)) +
          2.5 * Math.sin(barIndex * 1.9 + 1.2),
      ),
    ),
  ),
}));

// Created once at module scope: motion owns the `transform` on these nodes,
// so no static transform may ever come back into their `sx` — the two systems
// would fight over the same property.
const MotionStrip = motion.create(Box);
const MotionBar = motion.create(Box);
const MotionDot = motion.create(Box);

/**
 * MUI `visuallyHidden` equivalent, kept local to avoid the extra import.
 * Le misure come stringa: passate a sx, i numeri <= 1 sono percentuali
 * (width 1 → 100%) e margin: -1 diventa un intero spacing di -8px.
 */
const visuallyHiddenStyles = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

interface WaveBarsProps {
  reduceMotion: boolean;
}

/** One pass of the wave; rendered twice back to back for a seamless loop. */
function WaveBars({ reduceMotion }: WaveBarsProps) {
  return (
    <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: '3px' }}>
      {BAR_PATTERN.map(({ barId, heightPx }, barIndex) => (
        <MotionBar
          key={barId}
          style={{ height: heightPx }}
          animate={
            reduceMotion
              ? undefined
              : {
                  scaleY: [0.55, 1, 0.55],
                  transition: {
                    delay: (barIndex % BAR_PULSE_PHASES) * BAR_PULSE_STAGGER_SECONDS,
                    duration: BAR_PULSE_SECONDS,
                    ease: 'easeInOut',
                    repeat: Infinity,
                  },
                }
          }
          sx={{
            bgcolor: 'primary.main',
            borderRadius: '2px',
            width: '4px',
          }}
        />
      ))}
    </Box>
  );
}

/**
 * The recording state of the assistant composer, rendered as a drop-in
 * replacement for the text field slot: identical height (56px), identical
 * pill geometry, identical border. Swapping text for voice therefore moves
 * zero pixels anywhere in the panel — this is the fix for the old design,
 * where a caption row above the composer changed its height and made the
 * whole panel jump.
 *
 * Inside the slot: a pulsing red dot, the elapsed timer, and the bar strip
 * drifting left like a tape rolling on, masked so the trailing edge fades
 * into the past. Brand primary (Prospero purple) marks it as the assistant's
 * own channel; red stays reserved for "live" and the stop control. The full
 * status sentence is visually hidden on the `role="status"` root; the bars
 * are purely decorative. Under reduced motion the wave freezes into a static
 * indicator and the dot holds still.
 */
export function RecordingWaveform({ seconds }: { seconds: number }) {
  const { t } = useTranslation();
  const reduceMotion = usePrefersReducedMotion();
  const minutes = Math.floor(seconds / 60);
  const formattedTime = `${minutes}:${String(seconds % 60).padStart(2, '0')}`;

  return (
    <Fade appear in timeout={150}>
      <Box
        data-testid="recording-waveform"
        role="status"
        sx={{
          alignItems: 'center',
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'border.light',
          borderRadius: '28px',
          display: 'flex',
          gap: 1.25,
          height: 56,
          minWidth: 0,
          overflow: 'hidden',
          pl: 2.25,
          pr: 2,
          width: '100%',
        }}
      >
        <Typography sx={{ position: 'absolute', ...visuallyHiddenStyles }} variant="caption">
          {t('guida.assistant.recordingStatus', { defaultValue: 'Registrazione in corso' })}{' '}
          {formattedTime}
        </Typography>
        <MotionDot
          animate={reduceMotion ? undefined : { opacity: [1, 0.3, 1], scale: [1, 0.78, 1] }}
          aria-hidden="true"
          sx={{ bgcolor: 'error.main', borderRadius: '50%', flexShrink: 0, height: 9, width: 9 }}
          transition={{ duration: 1.6, ease: 'easeInOut', repeat: Infinity }}
        />
        <Typography
          aria-hidden="true"
          sx={{
            color: 'error.main',
            flexShrink: 0,
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 600,
            lineHeight: 1,
          }}
          variant="body2"
        >
          {formattedTime}
        </Typography>
        <Box
          aria-hidden="true"
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
          }}
        >
          <MotionStrip
            animate={
              reduceMotion
                ? undefined
                : {
                    x: '-50%',
                    transition: {
                      duration: STRIP_LOOP_SECONDS,
                      ease: 'linear',
                      repeat: Infinity,
                    },
                  }
            }
            sx={{ alignItems: 'center', display: 'flex', gap: '3px', width: 'max-content' }}
          >
            <WaveBars reduceMotion={reduceMotion} />
            <WaveBars reduceMotion={reduceMotion} />
          </MotionStrip>
        </Box>
      </Box>
    </Fade>
  );
}
